import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import {
  AvailabilityEditor,
  type ResourceBlockedTimeInput,
  type ResourceScheduleInput,
} from "@/components/pages/bookings/availability-editor";

type ResourceType = "person" | "room" | "equipment" | "vehicle" | "virtual";

interface CommissionConfig {
  type: "flat" | "percentage" | "tiered";
  value?: number;
}

interface ResourceRuntime {
  id: string;
  name: string;
  resourceType: ResourceType;
  skills: string[];
  capacity: number;
  isActive: boolean;
  publicName: string | null;
  publicBio: string | null;
  commissionConfig: CommissionConfig | null;
  schedules: ResourceScheduleInput[];
  blockedTimes: ResourceBlockedTimeInput[];
}

interface ResourceFormData {
  name: string;
  resourceType: ResourceType;
  skills: string[];
  capacity: number;
  isActive: boolean;
  publicName: string;
  publicBio: string;
  commissionType: "none" | "flat" | "percentage" | "tiered";
  commissionValue: number;
  schedules: ResourceScheduleInput[];
  blockedTimes: ResourceBlockedTimeInput[];
}

const TYPE_LABELS: Record<ResourceType, string> = {
  person: "Staff",
  room: "Room",
  equipment: "Equipment",
  vehicle: "Vehicle",
  virtual: "Virtual",
};

const TYPE_ICONS: Record<ResourceType, string> = {
  person: "👤",
  room: "🏠",
  equipment: "🔧",
  vehicle: "🚗",
  virtual: "💻",
};

function emptyForm(): ResourceFormData {
  return {
    name: "",
    resourceType: "person",
    skills: [],
    capacity: 1,
    isActive: true,
    publicName: "",
    publicBio: "",
    commissionType: "none",
    commissionValue: 0,
    schedules: [],
    blockedTimes: [],
  };
}

function toFormData(resource: ResourceRuntime): ResourceFormData {
  return {
    name: resource.name,
    resourceType: resource.resourceType,
    skills: resource.skills,
    capacity: resource.capacity,
    isActive: resource.isActive,
    publicName: resource.publicName ?? "",
    publicBio: resource.publicBio ?? "",
    commissionType: resource.commissionConfig?.type ?? "none",
    commissionValue: resource.commissionConfig?.value ?? 0,
    schedules: resource.schedules,
    blockedTimes: resource.blockedTimes,
  };
}

function toPayload(form: ResourceFormData) {
  return {
    name: form.name.trim(),
    resourceType: form.resourceType,
    skills: form.skills,
    capacity: form.capacity,
    isActive: form.isActive,
    publicName: form.publicName.trim() || null,
    publicBio: form.publicBio.trim() || null,
    commissionConfig: form.commissionType === "none"
      ? null
      : { type: form.commissionType, value: form.commissionValue },
    schedules: form.schedules,
    blockedTimes: form.blockedTimes.map((block) => ({
      startAt: new Date(block.startAt).toISOString(),
      endAt: new Date(block.endAt).toISOString(),
      reason: block.reason,
      notes: block.notes ?? null,
    })),
  };
}

export default function ResourceManagementPage() {
  const [resources, setResources] = useState<ResourceRuntime[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ResourceFormData>(emptyForm);
  const [newSkill, setNewSkill] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    apiFetch<{ resources: ResourceRuntime[] }>("/crm/bookings/resources")
      .then((response) => {
        setResources(response.resources);
        setError(null);
      })
      .catch((reason) => {
        setResources([]);
        setError(reason instanceof Error ? reason.message : "Could not load resources");
      })
      .finally(() => setLoading(false));
  }, []);

  const activeCount = useMemo(
    () => resources.filter((resource) => resource.isActive).length,
    [resources],
  );
  const groupedResources = useMemo(() => {
    return resources.reduce<Record<ResourceType, ResourceRuntime[]>>((groups, resource) => {
      const existing = groups[resource.resourceType] ?? [];
      existing.push(resource);
      groups[resource.resourceType] = existing;
      return groups;
    }, {
      person: [],
      room: [],
      equipment: [],
      vehicle: [],
      virtual: [],
    });
  }, [resources]);

  const handleCreate = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((resource: ResourceRuntime) => {
    setEditingId(resource.id);
    setForm(toFormData(resource));
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (editingId) {
        const updated = await apiFetch<{ resource: ResourceRuntime }>(`/crm/bookings/resources/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify(toPayload(form)),
        });
        setResources((current) => current.map((resource) => (
          resource.id === editingId ? updated.resource : resource
        )));
      } else {
        const created = await apiFetch<{ resource: ResourceRuntime }>("/crm/bookings/resources", {
          method: "POST",
          body: JSON.stringify(toPayload(form)),
        });
        setResources((current) => [...current, created.resource]);
      }
      setShowModal(false);
    } finally {
      setSaving(false);
    }
  }, [editingId, form]);

  const handleDelete = useCallback(async () => {
    if (!editingId) {
      return;
    }
    setDeleting(true);
    try {
      await apiFetch<{ data: { deleted: boolean; resourceId: string } }>(`/crm/bookings/resources/${editingId}`, {
        method: "DELETE",
      });
      setResources((current) => current.filter((resource) => resource.id !== editingId));
      setShowModal(false);
    } finally {
      setDeleting(false);
    }
  }, [editingId]);

  const addSkill = useCallback(() => {
    const next = newSkill.trim();
    if (!next || form.skills.includes(next)) {
      return;
    }
    setForm((current) => ({ ...current, skills: [...current.skills, next] }));
    setNewSkill("");
  }, [form.skills, newSkill]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Resources</h1>
          <p className="text-sm text-muted-foreground">
            {resources.length} total · {activeCount} active in the booking runtime
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Add resource
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/25 bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {resources.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <p className="text-lg font-medium text-foreground">No resources yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add staff members, rooms, equipment, or virtual providers before opening availability to customers.
          </p>
          <button
            type="button"
            onClick={handleCreate}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Add resource
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {(Object.keys(TYPE_LABELS) as ResourceType[]).map((resourceType) => {
            const items = groupedResources[resourceType];
            if (items.length === 0) {
              return null;
            }
            return (
              <section key={resourceType} className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{TYPE_ICONS[resourceType]}</span>
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {TYPE_LABELS[resourceType]}
                  </h2>
                </div>
                {items.map((resource) => (
                  <button
                    key={resource.id}
                    type="button"
                    onClick={() => handleEdit(resource)}
                    className="flex w-full items-start gap-4 rounded-lg border border-border p-4 text-left transition hover:border-primary/40 hover:bg-accent/20"
                  >
                    <span className="text-2xl">{TYPE_ICONS[resource.resourceType]}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground">{resource.name}</p>
                        <span className={`rounded-full px-2 py-0.5 text-xs ${
                          resource.isActive
                            ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                            : "bg-muted text-muted-foreground"
                        }`}
                        >
                          {resource.isActive ? "active" : "inactive"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {TYPE_LABELS[resource.resourceType]} · capacity {resource.capacity}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>{resource.schedules.length} weekly windows</span>
                        <span>{resource.blockedTimes.length} active blocks</span>
                        {resource.skills.length > 0 ? <span>{resource.skills.join(", ")}</span> : null}
                      </div>
                    </div>
                  </button>
                ))}
              </section>
            );
          })}
        </div>
      )}

      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div
            className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-background p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{editingId ? "Edit resource" : "Add resource"}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configure the real booking resource plus the weekly schedule and dated block times the availability engine reads.
                </p>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
              <div className="space-y-4 rounded-xl border border-border p-4">
                <div>
                  <label className="block text-sm font-medium text-foreground">Name</label>
                  <input
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-foreground">Type</span>
                    <select
                      value={form.resourceType}
                      onChange={(event) => setForm((current) => ({ ...current, resourceType: event.target.value as ResourceType }))}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      {Object.entries(TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-foreground">Capacity</span>
                    <input
                      type="number"
                      min={1}
                      value={form.capacity}
                      onChange={(event) => setForm((current) => ({ ...current, capacity: Math.max(1, Number.parseInt(event.target.value || "1", 10)) }))}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <label className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                  />
                  Active in availability search
                </label>

                <div>
                  <label className="block text-sm font-medium text-foreground">Skills</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      value={newSkill}
                      onChange={(event) => setNewSkill(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addSkill();
                        }
                      }}
                      className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                      placeholder="Add skill"
                    />
                    <button type="button" onClick={addSkill} className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">
                      Add
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.skills.map((skill) => (
                      <span key={skill} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs text-foreground">
                        {skill}
                        <button
                          type="button"
                          onClick={() => setForm((current) => ({ ...current, skills: current.skills.filter((value) => value !== skill) }))}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-foreground">Commission</span>
                    <select
                      value={form.commissionType}
                      onChange={(event) => setForm((current) => ({ ...current, commissionType: event.target.value as ResourceFormData["commissionType"] }))}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="none">None</option>
                      <option value="flat">Flat</option>
                      <option value="percentage">Percentage</option>
                      <option value="tiered">Tiered</option>
                    </select>
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="font-medium text-foreground">Value</span>
                    <input
                      type="number"
                      min={0}
                      value={form.commissionValue}
                      disabled={form.commissionType === "none"}
                      onChange={(event) => setForm((current) => ({ ...current, commissionValue: Number.parseFloat(event.target.value || "0") || 0 }))}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground">Public display name</label>
                  <input
                    value={form.publicName}
                    onChange={(event) => setForm((current) => ({ ...current, publicName: event.target.value }))}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground">Public bio</label>
                  <textarea
                    value={form.publicBio}
                    onChange={(event) => setForm((current) => ({ ...current, publicBio: event.target.value }))}
                    className="mt-1 min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    placeholder="Optional booking-facing description"
                  />
                </div>
              </div>

              <AvailabilityEditor
                schedules={form.schedules}
                blockedTimes={form.blockedTimes}
                onSchedulesChange={(next) => setForm((current) => ({ ...current, schedules: next }))}
                onBlockedTimesChange={(next) => setForm((current) => ({ ...current, blockedTimes: next }))}
              />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <div>
                {editingId ? (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="rounded-md border border-[var(--color-error)]/30 px-4 py-2 text-sm text-[var(--color-error)] hover:bg-[var(--color-error)]/10 disabled:opacity-50"
                  >
                    {deleting ? "Removing..." : "Delete resource"}
                  </button>
                ) : null}
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {saving ? "Saving..." : editingId ? "Update resource" : "Create resource"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
